/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {getPlatformDeviceTypes} from '@ion/core/src/services/Connect';
import {
  GenericObject,
  PlatformDeviceType,
  PlatformFileType
} from '@ion/core/src/types/entities';
import {
  LoadPlatformDeviceTypesResult
} from '@ion/core/src/types/services';
import {alphaSort} from '@ion/core/src/utils/SortUtils';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceTypesResult = UseDefaultQueryResult & {
  deviceTypes: PlatformDeviceType[];
};

/**
 * Provides a utility which allows for determining if the given Device Type
 * have version as a required field.
 *
 * @param   deviceType of selected task.
 * @param   fieldName of selected task
 * @returns `true` if the given device type have version as required field,
 *          otherwise, `false`.
 */
export const isRequiredField = (fileType: PlatformFileType, fieldName: string): boolean => {
  if (fileType?.inputFields && fieldName) {
    return fileType?.inputFields?.find(({name}) => name === fieldName)?.required;
  }
  return false;
};

/**
 * The useDeviceFiles hook provides a React Query hook for referencing device types.
 *
 * ```typescript
 *
 * import {useDeviceTypes} from '@ion/query/src/hooks/useDeviceTypes';
 *
 *  ...
 *
 *  const {deviceTypes} = useDeviceTypes();
 *
 * ```
 *
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns UseDeviceTypesResult containing the query results and the device types.
 */
export const useDeviceTypes = (
  enabled = true,
  useSeconds = true,
  staleTime = getStaleTime(1, useSeconds, false)
): UseDeviceTypesResult => {
  const query = useQuery([QueryKey.DEVICE_TYPES], async() => {
    const results = await getPlatformDeviceTypes();
    return alphaSort({items: results as unknown as GenericObject[]}) as unknown as LoadPlatformDeviceTypesResult;
  }, {staleTime: staleTime as number, enabled});

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  return createQueryResult(query, {
    deviceTypes : query.data || []
  }) as UseDeviceTypesResult;
};

export default useDeviceTypes;
